// Runtime hostname-based landing/app discriminator.
//
// Athrelaris ships a single bundle to two custom domains on the same
// Cloudflare Pages project:
//   - `athrelaris.com` → public landing surface. `/` renders the
//     landing component (hero + beats + codex CTAs). `/codex`,
//     `/coming-soon`, `/privacy` work. Private routes also exist in
//     the bundle but their `beforeLoad` calls `redirectIfLandingOnly`
//     to bounce visitors back to `/coming-soon` — Phase A keeps the
//     app host hidden, so we never link out to it from the landing
//     and we never let a hand-typed URL like `athrelaris.com/login`
//     resolve to a real auth form.
//   - `app.athrelaris.com` → full app surface. `/` redirects to
//     `/login` / `/home` / `/awakening` per session + bond. No
//     public link from anywhere; URL shared manually with the
//     knock-list testers.
//
// Local dev mirrors production by hostname (so dev habits transfer
// cleanly):
//   - `localhost` / `127.0.0.1` → landing mode (where the landing /
//     codex / privacy work iterates)
//   - `app.localhost` → app mode (where the awakening / login flow
//     iterates). Browsers resolve `*.localhost` → 127.0.0.1
//     automatically per RFC 6761; no /etc/hosts edits required.
//
// SSR / test environments where `window` is undefined fall back to
// landing mode — the safer default (no API call, no auth probe).

import { redirect } from '@tanstack/react-router';

const FULL_APP_HOSTS = new Set(['app.athrelaris.com', 'app.localhost']);

/**
 * True when the current request is on a landing surface (public on
 * `athrelaris.com`, or local dev on `localhost`). False on
 * `app.athrelaris.com` or `app.localhost` where the app proper lives.
 */
export function isLandingOnly(): boolean {
  if (typeof window === 'undefined') return true;
  return !FULL_APP_HOSTS.has(window.location.hostname);
}

/**
 * Resolve the API base URL at runtime, mirroring the hostname split
 * the routing layer uses. Hostname is the sole source of truth — the
 * previous `VITE_API_BASE_URL` env var was a build-time leftover from
 * Phase A when the API didn't have its own subdomain; it's no longer
 * read. Delete it from CF Pages env vars to avoid confusion.
 *
 *   - `app.athrelaris.com`  → `https://api.athrelaris.com`
 *   - `app.localhost` (dev) → `http://localhost:3000`
 *   - any other host        → `http://localhost:3000` (default; the
 *     landing-only hosts stub `useSession` and don't actually call
 *     the API anyway, so this branch is rarely exercised)
 */
export function apiBaseURL(): string {
  if (typeof window === 'undefined') return 'http://localhost:3000';
  const host = window.location.hostname;
  if (host === 'app.athrelaris.com') return 'https://api.athrelaris.com';
  // Local dev: when the frontend is on `app.localhost`, target the API on the
  // same hostname (different port) so the session cookie counts as same-site
  // and `SameSite=Lax` permits it on fetches. `app.localhost:3000` resolves to
  // 127.0.0.1 the same as `localhost:3000`, so the API listener is reachable.
  if (host === 'app.localhost') return 'http://app.localhost:3000';
  return 'http://localhost:3000';
}

/**
 * Route-level guard used by every private surface's `beforeLoad`.
 * On landing-only hostnames (`athrelaris.com` and `localhost` in dev),
 * the only public path is `/coming-soon` — any other private route
 * throws a redirect to `/coming-soon` *before* it renders, so a hand-
 * typed `athrelaris.com/login` never reaches a broken auth form and
 * never leaks the app host via a cross-subdomain redirect.
 *
 * Throws synchronously; usable directly as the first line of a
 * private route's `beforeLoad`.
 */
export function redirectIfLandingOnly(): void {
  if (isLandingOnly()) {
    throw redirect({ to: '/coming-soon' });
  }
}
